<template>
  <div class="roomPanel" :class="classes">
    <div class="roomPanel-heading">
      <span class="roomPanel-name">{{ room.name }}</span>
      <span class="roomPanel-seperator">/</span>
      <span class="roomPanel-label">{{ room.label }}</span>
    </div>
    <div class="roomPanel-center">
      <span class="roomPanel-bookingStatus">{{ bookingStatus }}</span>
      <span class="roomPanel-bookingSubtitle">{{ bookingSubtitle }}</span>
      <div class="roomPanel-currentInfo">
        <p v-if="room.eventsUnavailable" class="roomPanel-eventsUnavailable">
          Could not fetch room events
        </p>
        <EventRow
          v-else-if="currentEvent || injectedEvent"
          :event="currentEvent || injectedEvent"
          :is-current-event="true"
        />
      </div>
    </div>
    <div class="roomPanel-footer">
      <UpcomingEvents
        v-if="upcomingEvents.length > 0"
        :events="upcomingEvents"
        :current-event="currentEvent"
      />
      <NoEvents v-else />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import UpcomingEvents from '@/components/UpcomingEvents.vue'
import NoEvents from '@/components/NoEvents.vue'
import EventRow from '@/components/EventRow.vue'

const baseEvent = {
  created: null,
  name: 'Free',
  user: null,
  injectedEvent: true
}

function fillInEvents(room, allEvents) {
  const events = [...allEvents]

  let i = 0

  do {
    const e1 = events[i]
    const e2 = events[i + 1]

    if (!e1 || !e2) break

    const start1 = dayjs(e1.date)
    const start2 = dayjs(e2.date)

    const end1 = start1.add(e1.duration, 'm')

    if (start2.isAfter(end1.add(1, 'm'))) {
      events.splice(i + 1, 0, {
        ...baseEvent,
        date: end1.toISOString(),
        duration: start2.diff(end1, 'm'),
        room: room.id
      })
    }

    i++
  } while (i < events.length - 1)

  return events.slice(0, 4)
}

function eventEnd(event) {
  return dayjs(event.date).add(event.duration, 'minute')
}

const OCCUPANCY_KEY = 'Occupancy Sensor'

export default {
  components: { UpcomingEvents, NoEvents, EventRow },
  props: {
    room: { type: Object, required: true }
  },
  data() {
    return {
      currentEvent: null,
      injectedEvent: null,
      upcomingEvents: [],
      timerId: null
    }
  },
  computed: {
    bookingStatus() {
      if (this.currentEvent) {
        return this.isOccupied ? 'Booked' : 'Booked?'
      } else {
        return this.isOccupied ? 'Free?' : 'Free'
      }
    },
    bookingSubtitle() {
      if (this.currentEvent) {
        return this.isOccupied ? '' : 'not occupied'
      } else {
        return this.isOccupied ? 'might be occupied' : ''
      }
    },
    sensorData() {
      return (
        this.room.entity && this.$store.getters.entityData(this.room.entity)
      )
    },
    isOccupied() {
      return this.sensorData?.[OCCUPANCY_KEY]?.value === 1
    },
    occupiedStatus() {
      return this.isOccupied ? 'Occupied' : 'Vacant'
    },
    classes() {
      let state = 'is-green'

      if (this.currentEvent) {
        state = this.isOccupied ? 'is-red' : 'is-amber'
      } else {
        state = this.isOccupied ? 'is-amber' : 'is-green'
      }

      return [state]
    }
  },
  mounted() {
    this.$timer.addListener(this, () => this.updateStatus())
    this.updateStatus()

    if (this.room.entity) {
      this.$store.dispatch('fetchEntityData', this.room.entity)

      this.timerId = setInterval(
        () => this.$store.dispatch('fetchEntityData', this.room.entity),
        10000
      )
    }
  },
  destroyed() {
    this.$timer.removeListener(this)

    if (this.timerId) {
      clearInterval(this.timerId)
    }
  },
  methods: {
    updateStatus() {
      const now = dayjs()

      const events = this.room.events.filter(e => now.isBefore(eventEnd(e)))

      this.currentEvent = null
      this.injectedEvent = null
      this.upcomingEvents = fillInEvents(this.room, events)

      this.upcomingEvents.sort((a, b) => {
        return dayjs(a.date).isBefore(b)
      })

      for (const event of this.upcomingEvents) {
        const start = dayjs(event.date)
        const end = start.add(event.duration, 'minute')

        if (now.isAfter(start) && now.isBefore(end) && !event.injectedEvent) {
          this.currentEvent = event
          break
        }
      }

      this.upcomingEvents = this.upcomingEvents.filter(
        e => e !== this.currentEvent
      )

      if (this.currentEvent === null && this.upcomingEvents.length > 0) {
        const start = dayjs(this.upcomingEvents[0].date)
        this.injectedEvent = {
          ...baseEvent,
          date: now.toISOString(),
          duration: Math.ceil(start.diff(now, 'm', true)),
          room: this.room.id
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.roomPanel {
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: space-between;

  transition: 300ms background-color, 300ms color;

  &.is-red {
    background: $red;
    color: $white;
  }
  &.is-green {
    background: $green;
    color: $white;
  }
  &.is-amber {
    background: $amber;
    color: $black;
  }
}

.roomPanel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.roomPanel-heading {
  @include use-title-font;
  font-size: $size-title;
  font-weight: 700;
  padding: 0.5rem;
}

.roomPanel-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  background: transparentize(#232334, 0.6);
  padding: 1em;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
}

.roomPanel-bookingStatus {
  @include use-title-font;
  font-weight: 700;
  font-size: 6vw;
  padding: 0 0.5rem;
}

.roomPanel-bookingSubtitle {
  font-size: $size-body;
  font-style: italic;
  opacity: 0.75;
}

.roomPanel-currentInfo {
  position: absolute;
  font-size: $size-body;
  left: 1em;
  right: 1em;
  bottom: 0.5em;
}

.roomPanel-seperator {
  opacity: 0.7;
  padding: 0 0.5rem;
}

.roomPanel-label {
  font-family: $family-body;
  font-weight: 400;
}

.roomPanel-label:before,
.roomPanel-label:after {
  content: '"';
}

.roomPanel-eventsUnavailable {
  font-size: $size-body;
  text-align: center;
  background-color: $red;
  color: $white;
  padding: 0.3em;
  margin: 0 -0.3em;
  border-radius: 4px;
}

@include mobile {
  .roomPanel {
    max-height: 45vh;
    position: relative;

    &:not(:last-child) {
      padding: 1em 0.5;
      border-bottom: 2px dashed rgba(255, 255, 255, 0.6);
    }
  }
  .roomPanel-center {
    position: unset;
    padding-bottom: 2rem;
  }
  .roomPanel-footer {
    display: none;
  }
  .roomPanel-bookingStatus {
    font-size: 15vw;
  }
}

@include tablet {
  .roomPanel {
    &:not(:last-child) {
      border-right: 2px dashed rgba(255, 255, 255, 0.6);
    }
  }
  .roomPanel-footer {
    min-height: 10em;
  }
}
</style>
